import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';

export default function PrevisionsTresoreriePage() {
  const [soldes, setSoldes] = useState({ solde_caisse: 0, solde_banque: 0, total_disponible: 0 });
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([api.get('/comptabilite/tresorerie/soldes'), api.get('/comptabilite/tresorerie')]);
      setSoldes(s.data);
      setMouvements(m.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build 90-day projection
  const now = new Date();
  const days90 = [];
  let soldeProj = Number(soldes.total_disponible) || 0;

  // Calculate average daily flux from last 30 days
  const d30 = new Date(now); d30.setDate(d30.getDate() - 30);
  const recent = mouvements.filter(m => new Date(m.date_mouvement) >= d30);
  const fluxMoyen = recent.length > 0
    ? recent.reduce((s, m) => s + (m.type === 'entree' ? Number(m.montant) : -Number(m.montant)), 0) / 30
    : 0;

  for (let i = 0; i <= 90; i += 7) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    days90.push({ date: d.toISOString().split('T')[0], semaine: `S+${Math.floor(i / 7)}`, solde: Math.round(soldeProj + fluxMoyen * i) });
  }

  const soldeMin = Math.min(...days90.map(d => d.solde));
  const soldeMax = Math.max(...days90.map(d => d.solde));
  const solde90j = days90[days90.length - 1]?.solde || 0;

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--gss-text-light)' }}>Chargement...</p>;

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Solde actuel</span></div>
          <div className="stat-card-value" style={{ color: '#2e7d32' }}>{Number(soldes.total_disponible).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Flux moyen/jour</span></div>
          <div className="stat-card-value" style={{ color: fluxMoyen >= 0 ? '#2e7d32' : '#DC2626' }}>{Math.round(fluxMoyen).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA (30j glissants)</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Projection J+90</span></div>
          <div className="stat-card-value" style={{ color: solde90j >= 0 ? '#2e7d32' : '#DC2626' }}>{solde90j.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Solde min prevu</span></div>
          <div className="stat-card-value" style={{ color: soldeMin >= 0 ? '#f57c00' : '#DC2626' }}>{soldeMin.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
      </div>

      <div className="content-card">
        <div className="content-card-title">Previsions de tresorerie — 90 jours</div>
        <div style={{ marginTop: 16, padding: '0 16px' }}>
          {/* Simple bar chart */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200, borderBottom: '1px solid #ddd', position: 'relative' }}>
            {soldeMax > 0 && <div style={{ position: 'absolute', top: 0, left: 0, fontSize: 10, color: '#888' }}>{soldeMax.toLocaleString()}</div>}
            {days90.map((d, i) => {
              const range = soldeMax - Math.min(0, soldeMin);
              const height = range > 0 ? Math.max(2, ((d.solde - Math.min(0, soldeMin)) / range) * 180) : 50;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ width: '80%', height, background: d.solde >= 0 ? '#2e7d3240' : '#DC262640', borderRadius: '4px 4px 0 0', border: `1px solid ${d.solde >= 0 ? '#2e7d32' : '#DC2626'}`, borderBottom: 'none' }} title={`${d.date}: ${d.solde.toLocaleString()} F`} />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {days90.map((d, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: '#888' }}>{d.semaine}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: 16 }}>
        <div className="content-card-title">Detail par semaine</div>
        <div className="gss-table-wrap">
          <table className="gss-table">
            <thead><tr><th>Semaine</th><th>Date</th><th>Solde projete</th><th>Variation</th></tr></thead>
            <tbody>
              {days90.map((d, i) => (
                <tr key={i}>
                  <td>{d.semaine}</td>
                  <td>{d.date}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: d.solde >= 0 ? '#2e7d32' : '#DC2626' }}>{d.solde.toLocaleString()} F</td>
                  <td style={{ textAlign: 'right', fontSize: 12, color: '#888' }}>
                    {i > 0 ? `${(d.solde - days90[i - 1].solde >= 0 ? '+' : '')}${(d.solde - days90[i - 1].solde).toLocaleString()} F` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ padding: '1rem', fontSize: 11, color: '#888', fontStyle: 'italic' }}>
        Previsions basees sur le flux moyen journalier des 30 derniers jours. A titre indicatif uniquement.
      </div>
    </div>
  );
}
